import { mapState, mapGetters, mapActions } from 'vuex'
import {
  MODULE,
  LOGIN,
  RESET_PASSWORD,
  UPDATE_PASSWORD,
  LOGOUT,
  REFRESH_TOKEN,
} from '@/state/modules/auth'
import {
  USERS_MODULE,
  GET_ALL_USER,
  GET_USER,
  CREATE_USER,
  UPDATE_USER,
  UPDATE_USER_STATUS,
  UPDATE_USER_LOGIN_ACCESS,
  UNLOCK_USER,
} from '@/state/modules/users'
import {
  ROLE_MODULE,
  GET_ALL_ROLE,
  GET_ROLE,
  GET_PERMISSIONS,
  CREATE_ROLE,
  UPDATE_ROLE,
  UPDATE_ROLE_STATUS,
} from '@/state/modules/role'
import {
  CONFIGURATION_MODULE,
  GET_ALL_CONFIGURATION,
  GET_CONFIGURATION,
  UPDATE_CONFIGURATION,
} from '@/state/modules/configuration'
import {
  ORGANIZATION_MODULE,
  GET_ALL_ORGANIZATION,
  GET_ORGANIZATION,
  CREATE_ORGANIZATION,
  UPDATE_ORGANIZATION_STATUS,
} from '@/state/modules/organization'

/**
 * Which stores the pages reach through.
 *
 * ── Two kinds of screen, on purpose ─────────────────────────────────────
 * The DESK screens — inbox, knowledge base, bot health — call their services
 * directly and hold their own component state. They are request/response
 * against the server, not shared client state, and a store module per screen
 * would be ceremony around a single `await`. That has not changed.
 *
 * The ADMIN screens — users, roles, configuration, organisations, company
 * profile — do go through vuex, because they genuinely share state: a listing
 * holds search criteria across a navigation to a form and back, a row toggle
 * has to re-read the list it is showing, and the role editor's permission
 * vocabulary is used by two screens. That is one_sales' pattern and these
 * screens are ported from it, so the mapped names below match the ones its
 * pages call.
 *
 * Auth stays in vuex because the router guards and apiHelper both read it.
 */

export const authComputed = {
  ...mapState(MODULE, {
    currentUser: (state) => state.currentUser,
  }),
  ...mapGetters(MODULE, ['loggedIn']),
}

/**
 * Names here must match what the pages call — login.vue does
 * `this.login({ data })`, not `this.logIn(...)`. Renaming them is how you get
 * "this.login is not a function", which Vue reports only when the button is
 * pressed, not at build time.
 */
export const authMethods = mapActions(MODULE, {
  login: LOGIN,
  logout: LOGOUT,
  resetPassword: RESET_PASSWORD,
  updatePassword: UPDATE_PASSWORD,
  refreshToken: REFRESH_TOKEN,
})

export const layoutComputed = {
  ...mapState('layout', {
    layoutType: (state) => state.layoutType,
    leftSidebarType: (state) => state.leftSidebarType,
    layoutWidth: (state) => state.layoutWidth,
    topbar: (state) => state.topbar,
    loader: (state) => state.loader,
  }),
}

/**
 * Kept although nothing spreads it today: the layout store still owns these and
 * a settings panel would reach for them. It costs nothing and removing it is a
 * second edit when someone needs it back.
 */
export const layoutMethods = mapActions('layout', [
  'changeLayoutType',
  'changeLayoutWidth',
  'changeLeftSidebarType',
  'changeTopbar',
  'changeLoaderValue',
])

/*
|------------------------------------------------------------------------------
| Admin screens
|------------------------------------------------------------------------------
|
| The KEY is the name a page calls (`this.getAllUser(...)`); the VALUE is the
| action constant. Mismatching them is how you get "this.getAllUser is not a
| function" — which Vue only reports when the button is pressed, not at build
| time. The keys deliberately match one_sales' so its pages port unchanged.
*/

export const usersComputed = {
  ...mapState(USERS_MODULE, {
    userList: (state) => state.list,
    userDetails: (state) => state.details,
    userStatus: (state) => state.status,
  }),
}

export const usersMethods = mapActions(USERS_MODULE, {
  getAllUser: GET_ALL_USER,
  getUsers: GET_USER,
  createUsers: CREATE_USER,
  updateUsers: UPDATE_USER,
  updateUsersStatus: UPDATE_USER_STATUS,
  updateUsersLoginAccess: UPDATE_USER_LOGIN_ACCESS,
  unlockUsers: UNLOCK_USER,
})

export const roleMethods = mapActions(ROLE_MODULE, {
  getAllRole: GET_ALL_ROLE,
  getRole: GET_ROLE,
  // Named `lookupPermission` to match what one_sales' roleForm calls, where it
  // lived on a separate lookup module this app does not have.
  lookupPermission: GET_PERMISSIONS,
  createRole: CREATE_ROLE,
  updateRole: UPDATE_ROLE,
  updateRoleStatus: UPDATE_ROLE_STATUS,
})

export const configurationMethods = mapActions(CONFIGURATION_MODULE, {
  getAllConfiguration: GET_ALL_CONFIGURATION,
  getConfiguration: GET_CONFIGURATION,
  updateConfiguration: UPDATE_CONFIGURATION,
})

export const organizationMethods = mapActions(ORGANIZATION_MODULE, {
  getAllOrganization: GET_ALL_ORGANIZATION,
  getOrganization: GET_ORGANIZATION,
  createOrganization: CREATE_ORGANIZATION,
  updateOrganizationStatus: UPDATE_ORGANIZATION_STATUS,
})
